import React, {useCallback, useState} from "react";
import {useAdmin} from "../../../contexts/AdminContext";
import Heading from "../../../components/Heading";
import Button from "../../../components/Button";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../../components/IconButton";
import {useTranslation} from "react-i18next";
import {useGlobalContext} from "../../../contexts/globalContext";
import Input from "../../../components/Input";
import Card from "../../../components/Card";
import Text from "../../../components/Text";
import CodeInput from "../../../components/CodeInput";

const PasswordReset: React.FC<{changePage: (p: string) => void}> = ({changePage}) => {
    const { service, changeUser } = useAdmin()
    const {t} = useTranslation(['settings'])
    const {showToast} = useGlobalContext()
    const [email, setEmail] = useState<string>('')
    const [form, setForm] = useState<'email'|'code'>('email')

    const [resetData, setResetData] = useState<Partial<{
        token: string,
        password: string,
        password_confirmation: string
    }>>({})

    const submit = useCallback(() => {
        service.passwordReset({
            email: email
        }).then(() => {
            setEmail('')
            showToast('top-right', 'success', t('subscription.passwordReset.success'))
            setForm('code')
        }).catch(() => {
            showToast('top-right', 'error', t('subscription.passwordReset.error'))
        })
    }, [service, email, changePage, changeUser, showToast, t])

    const submitCode = useCallback(() => {
        service.passwordResetConfirm(resetData).then(() => {
            showToast('top-right', 'success', t('subscription.passwordReset.changeSuccess'))
            changePage("overview")
        }).catch(() => {
            showToast('top-right', 'error', t('subscription.passwordReset.changeError'))
        })
    }, [service, resetData, t])

    return (
        <div className={'mt-3'}>
            <Heading level={3} classes={'mb-4'}>{t('subscription.passwordReset.heading')}</Heading>
            <IconButton
                icon={faArrowLeft}
                color={'black'}
                onClick={() => changePage('overview')}
            >
                {t("subscription.back")}
            </IconButton>
            {form === 'email' ?
                <>
                    <Card title={t("subscription.passwordReset.resetText")} classes={'my-3'}>
                        <Text>{t('subscription.passwordReset.description')}</Text>
                        <div className={'row'}>
                            <div className={'col-12 my-3'}>
                                <Input
                                    type={'email'}
                                    placeholder={'E-Mail'}
                                    property={'email'}
                                    value={email}
                                    onChange={(property, value) => setEmail(value)}
                                />
                            </div>
                        </div>
                    </Card>
                    <div className={'mt-4 d-flex justify-content-between'}>
                        <Button
                            hierarchy={'tertiary'}
                            text={t("subscription.passwordReset.buttons.alreadyHaveCode")}
                            fn={() => setForm('code')}
                        />
                        <Button
                            hierarchy={'primary'}
                            text={t('subscription.passwordReset.buttons.confirmReset')}
                            fn={() => submit()}
                        />
                    </div>
                </> :
                <>
                    <Card title={t("subscription.passwordReset.newPasswordText")} classes={'my-3'}>
                        <div className={'row'}>
                            <div style={{gap: '2rem'}} className={'d-flex flex-column col-12 my-3'}>
                                <CodeInput
                                    property={'token'}
                                    value={resetData.token ?? ''}
                                    label={'Reset-Code'}
                                    onChange={(property, value) => setResetData(prev => {return ({...prev, [property]: value})})}
                                />
                                <Input
                                    type={'password'}
                                    placeholder={t('subscription.passwordReset.password')}
                                    property={'password'}
                                    value={resetData.password ?? ''}
                                    onChange={(property, value) => setResetData(prev => {return ({...prev, [property]: value})})}
                                />
                                <Input
                                    type={'password'}
                                    placeholder={t('subscription.passwordReset.passwordConfirmation')}
                                    property={'password_confirmation'}
                                    value={resetData.password_confirmation ?? ''}
                                    onChange={(property, value) => setResetData(prev => {return ({...prev, [property]: value})})}
                                />
                            </div>
                        </div>
                    </Card>
                    <div className={'mt-4 d-flex justify-content-between'}>
                        <Button
                            hierarchy={'tertiary'}
                            text={t("subscription.passwordReset.buttons.noCode")}
                            fn={() => setForm('email')}
                        />
                        <Button
                            hierarchy={'primary'}
                            text={t('subscription.passwordReset.buttons.confirmPassword')}
                            fn={() => submitCode()}
                            disabled={
                                resetData.token === undefined ||
                                resetData.password === undefined ||
                                resetData.password_confirmation === undefined ||
                                resetData.token === '' ||
                                resetData.password === '' ||
                                resetData.password_confirmation === '' ||
                                resetData.password !== resetData.password_confirmation ||
                                resetData.password.length < 8
                            }
                        />
                    </div>
                </>
            }

        </div>
    )
}

export default PasswordReset
